"use client";

import { SearchZoomIn, SearchZoomOut } from "iconsax-reactjs";
import { useTranslations } from "next-intl";
import { useCallback, useState } from "react";
import Cropper, { type Area, type Point } from "react-easy-crop";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { blobToFile, getCroppedImg } from "@/lib/utils/crop-image";

export interface ImageCropDialogProps {
  /** The image source URL to crop */
  image: string;
  /** Aspect ratio for the crop area (default: 1 for square) */
  aspectRatio?: number;
  /** Shape of the crop area */
  cropShape?: "rect" | "round";
  /** Title shown in the dialog header */
  title?: string;
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Callback when crop is confirmed with the cropped file */
  onCropComplete: (croppedFile: File) => void;
  /** Original filename to preserve (default: cropped-image.jpg) */
  originalFileName?: string;
  /** Minimum zoom level (default: 1) */
  minZoom?: number;
  /** Maximum zoom level (default: 3) */
  maxZoom?: number;
  /** Maximum optimized output size in MB */
  maxSizeMB?: number;
}

export const ImageCropDialog = ({
  image,
  aspectRatio = 1,
  cropShape = "rect",
  title,
  open,
  onOpenChange,
  onCropComplete,
  originalFileName = "cropped-image.jpg",
  minZoom = 1,
  maxZoom = 3,
  maxSizeMB = 5,
}: ImageCropDialogProps) => {
  const t = useTranslations("common.imageCrop");
  const resolvedTitle = title ?? t("cropImage");
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const onCropChange = useCallback((location: Point) => {
    setCrop(location);
  }, []);

  const onZoomChange = useCallback((newZoom: number) => {
    setZoom(newZoom);
  }, []);

  const handleCropComplete = useCallback(
    (_croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    [],
  );

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;

    setIsProcessing(true);
    try {
      // Crop, resize, and convert to WebP
      const croppedBlob = await getCroppedImg(
        image,
        croppedAreaPixels,
        0,
        { horizontal: false, vertical: false },
        {
          maxWidth: 2048,
          maxHeight: 2048,
          quality: 0.88,
          format: "webp",
          maxSizeMB,
        },
      );
      if (croppedBlob) {
        // Generate filename with timestamp
        const timestamp = Date.now();
        const baseName = originalFileName.replace(/\.[^/.]+$/, "");
        const fileName = `${baseName}-cropped-${timestamp}`;

        // blobToFile will add .webp extension
        const croppedFile = blobToFile(croppedBlob, fileName, "webp");
        onCropComplete(croppedFile);
        onOpenChange(false);
        // Reset state
        setCrop({ x: 0, y: 0 });
        setZoom(1);
      }
    } catch (error) {
      console.error("Error cropping image:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    // Reset state
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  // Zoom slider step calculation
  const zoomStep = (maxZoom - minZoom) / 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          "w-[calc(100vw-2rem)] max-w-md p-0 overflow-hidden",
          "sm:max-w-lg",
        )}
      >
        <DialogHeader className="px-4 pt-4 pb-2">
          <DialogTitle className="text-center text-text-primary">
            {resolvedTitle}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t("descSingle")}
          </DialogDescription>
        </DialogHeader>

        {/* Cropper Container */}
        <div
          className={cn("relative w-full bg-bg-primary", "h-[280px] sm:h-80")}
        >
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            cropShape={cropShape}
            showGrid={cropShape === "rect"}
            onCropChange={onCropChange}
            onCropComplete={handleCropComplete}
            onZoomChange={onZoomChange}
            minZoom={minZoom}
            maxZoom={maxZoom}
            objectFit="contain"
            classes={{
              containerClassName: "!bg-bg-primary",
              cropAreaClassName: cn(
                cropShape === "round"
                  ? "!border-2 !border-accent"
                  : "!border-2 !border-dashed !border-accent",
              ),
            }}
          />
        </div>

        {/* Zoom Controls */}
        <div className="px-4 py-3 space-y-2">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setZoom(Math.max(minZoom, zoom - 0.1))}
              className="p-1.5 rounded-xs bg-bg-tertiary/60 text-text-primary hover:bg-bg-tertiary transition-colors"
              aria-label={t("zoomOut")}
            >
              <SearchZoomOut size={20} />
            </button>

            {/* Custom Range Slider */}
            <div className="flex-1 relative h-8 flex items-center">
              <div className="absolute inset-x-0 h-1.5 bg-bg-tertiary rounded-full" />
              <div
                className="absolute h-1.5 bg-accent rounded-full"
                style={{
                  width: `${((zoom - minZoom) / (maxZoom - minZoom)) * 100}%`,
                }}
              />
              <input
                type="range"
                min={minZoom}
                max={maxZoom}
                step={zoomStep}
                value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))}
                className={cn(
                  "absolute inset-x-0 w-full h-8 appearance-none bg-transparent cursor-pointer",
                  // Thumb styling
                  "[&::-webkit-slider-thumb]:appearance-none",
                  "[&::-webkit-slider-thumb]:w-5",
                  "[&::-webkit-slider-thumb]:h-5",
                  "[&::-webkit-slider-thumb]:rounded-full",
                  "[&::-webkit-slider-thumb]:bg-accent",
                  "[&::-webkit-slider-thumb]:border-2",
                  "[&::-webkit-slider-thumb]:border-bg-primary",
                  "[&::-webkit-slider-thumb]:shadow-md",
                  "[&::-webkit-slider-thumb]:cursor-pointer",
                  "[&::-webkit-slider-thumb]:transition-transform",
                  "[&::-webkit-slider-thumb]:hover:scale-110",
                  // Firefox thumb
                  "[&::-moz-range-thumb]:w-5",
                  "[&::-moz-range-thumb]:h-5",
                  "[&::-moz-range-thumb]:rounded-full",
                  "[&::-moz-range-thumb]:bg-accent",
                  "[&::-moz-range-thumb]:border-2",
                  "[&::-moz-range-thumb]:border-bg-primary",
                  "[&::-moz-range-thumb]:shadow-md",
                  "[&::-moz-range-thumb]:cursor-pointer",
                  // Track styling (hide default)
                  "[&::-webkit-slider-runnable-track]:bg-transparent",
                  "[&::-moz-range-track]:bg-transparent",
                )}
                aria-label={t("zoomLevel")}
              />
            </div>

            <button
              type="button"
              onClick={() => setZoom(Math.min(maxZoom, zoom + 0.1))}
              className="p-1.5 rounded-xs bg-bg-tertiary/60 text-text-primary hover:bg-bg-tertiary transition-colors"
              aria-label={t("zoomIn")}
            >
              <SearchZoomIn size={20} />
            </button>
          </div>

          <p className="text-xs text-text-secondary text-center">
            {t("dragHint")}
          </p>
        </div>

        <DialogFooter className="px-4 pb-4 flex-row gap-3">
          <Button
            type="button"
            variant="ghost"
            colors="secondary"
            onClick={handleCancel}
            className="flex-1"
            disabled={isProcessing}
          >
            {t("cancel")}
          </Button>
          <Button
            type="button"
            variant="solid"
            colors="primary"
            onClick={handleConfirm}
            className="flex-1"
            loading={isProcessing}
          >
            {t("confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
